import { Component, Input, OnInit, OnDestroy, signal, computed, inject, DestroyRef, Output, EventEmitter } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconComponent } from '../icon/icon.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToastConfig, ToastInsideConfig, ToastTheme, TypeToast } from './toast.config';
import { CommonModule } from '@angular/common';
import { ToasService } from './service/toas-service.service';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PipesModule } from '../../pipes/pipe-module';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { NotificationService } from 'src/app/core/service/notification.service';

@Component({
  selector: 'mg-toast-alert',
  templateUrl: './toast-alert.component.html',
  styleUrls: ['./toast-alert.component.scss'],
  standalone: true,
  imports: [
    IconComponent,
    MatProgressBarModule,
    MatCheckboxModule,
    MatTooltipModule,
    CommonModule,
    FormsModule,
    PipesModule,
    ReactiveFormsModule
  ],
  animations: [
    trigger('toastAnimation', [
      state('void', style({
        transform: 'translateY(100%)',
        opacity: 0,
        visibility: 'hidden'
      })),
      state('visible', style({
        transform: 'translateY(0)',
        opacity: 1,
        visibility: 'visible'
      })),
      state('hidden', style({
        transform: 'translateY(-100%)',
        opacity: 0,
        display: 'absolute',
        zIndex: -99,
        height: 0,
        margin: 0,
        padding: 0
      })),
      transition('void => visible', [
        animate('200ms ease-out')
      ]),
      transition('visible => hidden', [
        animate('200ms ease-out')
      ])
    ])
  ]
})
export class ToastAlertComponent implements OnInit, OnDestroy {
  @Input() toastData!: ToastInsideConfig;
  @Output() toastClosed = new EventEmitter<ToastInsideConfig>();
  @Output() markAsRead = new EventEmitter<number>(); // Emitir cuando se marca como leído

  readonly progressBar = signal<number>(100);
  readonly showRedirect = signal<boolean>(false);
  readonly animationState = signal<'visible' | 'hidden'>('visible');

  labelCheck: string = '';
  typeToast: TypeToast = 'warning';
  label: string | undefined = undefined;
  body: string | undefined = undefined;
  theme!: ToastConfig;
  position!: string;
  formToast!: FormGroup;
  checkbox: boolean = false;

  private rafId: number | null = null;
  private startTime: number | null = null;
  private durationMs: number = 0;

  private readonly destroyRef = inject(DestroyRef);
  private readonly _toastService = inject(ToasService);
  private readonly _notificationService = inject(NotificationService);

  readonly isManualProgress = computed(() => this.toastData?.manualProgress ?? false);
  readonly showProgressBar = computed(() => !!(this.toastData?.timer || this.toastData?.manualProgress));

  constructor() {
  }

  ngOnInit(): void {
    this._toastService.closeAllToastObs.subscribe(() => this.closeToast());
    this.initToastData();
    this.animationState.set('visible');
  }

  ngOnDestroy(): void {
    this.cancelRaf();
  }

  private initFormToast(): void {
    this.formToast = new FormGroup({
      isChecked: new FormControl(false),
      confirmRedirection: new FormControl(false)
    });
  }

  initToastData(): void {
    this.initFormToast();
    this.toastData.redirectionButtom = this.toastData.redirectionButtom ?? true;
    this.typeToast = this.toastData.type ?? this.typeToast;
    this.labelCheck = this.toastData.labelCheck ?? '';
    this.theme = ToastTheme[this.typeToast];
    this.label = this.toastData.title;
    this.body = this.toastData.message ?? '';
    this.position = this.toastData.positionCss!.init;

    if (this.toastData.manualProgress) {
      this.progressBar.set(this.toastData.progress ?? 0);
      this._toastService.progressObs
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((evt) => {
          if (evt && evt.count === this.toastData.count) {
            this.progressBar.set(evt.progress);
          }
        });
    } else if (this.toastData.timer) {
      this.durationMs = this.toastData.timer * 1000;
      this.startRaf();
    }

    setTimeout(() => this.showRedirect.set(this.toastData.redirectionButtom!), 500);
  }

  private startRaf(): void {
    const tick = (timestamp: number) => {
      if (!this.startTime) this.startTime = timestamp;

      const elapsed = timestamp - this.startTime;
      const remaining = Math.max(0, 100 - (elapsed / this.durationMs) * 100);

      this.progressBar.set(remaining);

      if (remaining > 0) {
        this.rafId = requestAnimationFrame(tick);
      } else {
        this.closeToast();
      }
    };

    this.rafId = requestAnimationFrame(tick);
  }

  private cancelRaf(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  closeToast(): void {
    this.cancelRaf();
    this.progressBar.set(0);
    this.animationState.set('hidden');
    this.position = this.toastData.positionCss?.end ?? 'init-top-r';
  }

  onAnimationDone(event: any): void {
    if (this.animationState() === 'hidden') {
      // Si el checkbox está marcado, emitir evento para marcar como leído
      if (this.formToast.get('isChecked')!.value && this.toastData.notificationId) {
        this.markAsRead.emit(this.toastData.notificationId);
      }

      this.toastClosed.emit({
        ...this.toastData,
        bMostrar: false,
        isChecked: this.formToast.get('isChecked')!.value,
        confirmRedirection: this.formToast.get('confirmRedirection')!.value
      });
    }
  }

  handleRedirect(): void {
    if (this.toastData.notificationId) {
      this._notificationService.markNotificationsAsRead([this.toastData.notificationId]).subscribe();
    }

    if (this.toastData.sUrlredirection) {
      window.open(this.toastData.sUrlredirection, '_blank');
    }
    this.formToast.get('confirmRedirection')?.patchValue(true);
    this.closeToast();
  }

  handleCheck(e: Event): void {
    const isChecked = (e.target as HTMLInputElement).checked;
    this.formToast.get('isChecked')?.patchValue(isChecked);
    
    if (isChecked && this.toastData.notificationId) {
      this._notificationService.markNotificationsAsRead([this.toastData.notificationId]).subscribe({
        next: (resp) => {
          if (resp.isSuccess) {
            this.formToast.get('isChecked')?.disable()
          }
        },
        error: (err) => {
          console.error('Error al marcar como leído:', err);
          this.formToast.get('isChecked')?.patchValue(false);
        }
      });
    }
  }
}